import { Form, Formik } from 'formik';
import * as bd from 'react-basic-design';
import { BoardDTO } from '../../../api/board-api';
import { ContractDTO } from '../../../api/contract-api';
import { CustomerDTO } from '../../../api/customer-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDelete from '../../../assets/icons/Delete';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { T, translate } from '../../../components/basic/text';
import { yup } from '../../../components/basic/yup';
import { FormikDateInput, FormikTextField } from '../../../components/formik';
import { calculateContractPeriod } from './contract-helper';

interface SaveContractModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedContract?: ContractDTO;
    boards: BoardDTO[];
    customers: CustomerDTO[];
    onHide: () => void;
    onSave: (values: ContractDTO) => void;
    onDelete?: (contract: ContractDTO) => void;
}

export function SaveContractModal({
    show,
    allowEdit,
    selectedContract,
    boards,
    customers,
    onHide,
    onSave,
    onDelete,
}: SaveContractModalProps) {
    const ContractSchema = yup.object().shape({
        boardId: yup.number().required(' '),
        customerId: yup.number().required(' '),
        startDate: yup.date().required(' '),
        endDate: yup.date().required(' '),
        price: yup.number().required(' '),
    });

    const defaultValues: ContractDTO = {
        id: selectedContract?.id,

        boardId: selectedContract ? selectedContract.boardId : 0,
        customerId: selectedContract ? selectedContract.customerId : 0,
        startDate: selectedContract ? selectedContract.startDate : new Date(),
        endDate: selectedContract ? selectedContract.endDate : new Date(),
        price: selectedContract ? selectedContract.price : 0,
    };

    if (!show) return <></>;

    return (
        <Formik initialValues={defaultValues} validationSchema={ContractSchema} onSubmit={onSave}>
            {formik => (
                <BasicModal
                    show={show}
                    onHide={onHide}
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <T>{selectedContract ? 'edit-contract' : 'new-contract'}</T>
                            <bd.Flex align="center" gap={2}>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={formik.submitForm}
                                    disabled={!allowEdit}
                                >
                                    <SvgSave />
                                </bd.Button>
                                {selectedContract && onDelete && (
                                    <bd.Button
                                        disabled={!allowEdit}
                                        variant="icon"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => onDelete(selectedContract)}
                                    >
                                        <SvgDelete />
                                    </bd.Button>
                                )}
                                <bd.Button
                                    disabled={!allowEdit}
                                    variant="icon"
                                    color="secondary"
                                    size="sm"
                                    onClick={onHide}
                                >
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex align="center" wrap>
                            <FormikTextField
                                label="board-id"
                                name="boardId"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...boards.map(board => ({
                                        id: board.id,
                                        title: `${board.title} `,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="customer-id"
                                name="customerId"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...customers.map(customer => ({
                                        id: customer.id,
                                        title: `${customer.firstName} ${customer.lastName} ${customer.companyName ? `(${customer.companyName})` : ''}`,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />
                            <bd.Flex align="center">
                                <FormikDateInput
                                    label="start-date"
                                    name="startDate"
                                    readOnly={!allowEdit}
                                    inputMaxWidth="11rem"
                                />
                                <FormikDateInput
                                    label="end-date"
                                    name="endDate"
                                    readOnly={!allowEdit}
                                    inputMaxWidth="11rem"
                                />
                            </bd.Flex>
                            <FormikTextField
                                label="price"
                                name="price"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />
                            <bd.FormRow label={translate('contract-period')}>
                                {calculateContractPeriod(
                                    formik.values.startDate,
                                    formik.values.endDate
                                )}
                            </bd.FormRow>
                        </bd.Flex>
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
}
